/**
 * IsochroneLayer.tsx — drive-time isochrone layer (T-135 / BEAD-0075)
 *
 * Independent null-rendering layer (mirrors EVChargerLayer): owns its own
 * source/layer lifecycle and fetches POST /api/isochrone when both an active
 * location and a duration (minutes) are set. Renders nothing until the user opts
 * in via the Off·5·10·15 control.
 *
 * Shape:
 *  - One geojson source + a fill layer (semi-transparent teal, sits above the tract
 *    choropleth so tract colors still read through) + a line layer outlining it.
 *  - Effect keyed [map, location, minutes]: fetches the isochrone and setData()s the
 *    Feature; minutes=null or location=null clears the source (renders nothing) with
 *    no network call; a null geojson (no reachable-area data) also clears it.
 *  - AbortController per fetch (a pin move / duration change aborts the in-flight one),
 *    guarded cleanup (isMapAlive + getLayer/getSource) exactly like EVChargerLayer
 *    (BEAD-0037), and a non-fatal error toast with Retry on failure.
 *
 * Theme (T-160 survival): MapLibre paint can't resolve CSS vars (PinMarker D-10), so
 * colors are JS hex literals chosen at mount from the `.dark` DOM class. On a live
 * basemap swap MapView remounts the whole layer subtree via key={styleEpoch}, so this
 * layer re-adds its source/layers and re-reads `.dark` for the new basemap palette —
 * the same mechanism that carries the other layers.
 */
import { useEffect, useRef } from 'react'
import type { GeoJSONSource } from 'maplibre-gl'
import { useMap } from './MapContext'
import { toast } from '../ui/toaster'
import { fetchIsochrone } from '../../lib/api'
import { isMapAlive } from '../../lib/mapLiveness'

export interface IsochroneLayerProps {
  location: { lat: number; lng: number } | null
  minutes: 5 | 10 | 15 | null
}

const SOURCE_ID = 'isochrone'
const LAYER_FILL_ID = 'isochrone-fill'
const LAYER_LINE_ID = 'isochrone-line'
const ALL_LAYER_IDS = [LAYER_FILL_ID, LAYER_LINE_ID]

const EMPTY_FC: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] }

// Brand teal --primary (#45b4ac). Fill opacity is bumped on dark so it reads against
// the Dark Matter basemap; the outline flips to a lighter/darker teal per theme.
interface IsochronePalette {
  fillColor: string
  fillOpacity: number
  lineColor: string
  lineOpacity: number
  lineWidth: number
}
const LIGHT_PALETTE: IsochronePalette = {
  fillColor: '#45b4ac',
  fillOpacity: 0.15,
  lineColor: '#2f8f88', // darker teal on the light (Positron) basemap
  lineOpacity: 0.9,
  lineWidth: 2,
}
const DARK_PALETTE: IsochronePalette = {
  fillColor: '#45b4ac',
  fillOpacity: 0.22, // bumped so it reads on the dark basemap
  lineColor: '#6fd3cb', // lighter teal on the dark (Dark Matter) basemap
  lineOpacity: 0.95,
  lineWidth: 2,
}

// T-160: read the theme actually in effect from the `.dark` DOM class — the single
// source of truth the ThemeToggle + system-pref listener both write (lib/theme.ts).
function paletteFromDom(): IsochronePalette {
  const dark =
    typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
  return dark ? DARK_PALETTE : LIGHT_PALETTE
}

export function IsochroneLayer({ location, minutes }: IsochroneLayerProps): null {
  const map = useMap()
  const initializedRef = useRef(false)
  // Reassigned by each fetch effect so a persistent error toast's Retry always refetches
  // the CURRENT (location, minutes) instead of dead-no-opping on a stale controller.
  const retryRef = useRef<() => void>(() => {})

  // ── One-time source + layer setup (palette read from the current basemap theme) ──
  useEffect(() => {
    if (!map || initializedRef.current) return
    initializedRef.current = true
    const palette = paletteFromDom()

    map.addSource(SOURCE_ID, { type: 'geojson', data: EMPTY_FC })

    // Fill sits above the tract choropleth (semi-transparent so tract colors read through).
    map.addLayer({
      id: LAYER_FILL_ID,
      type: 'fill',
      source: SOURCE_ID,
      paint: {
        'fill-color': palette.fillColor,
        'fill-opacity': palette.fillOpacity,
      },
    })
    // Outline on top of the fill.
    map.addLayer({
      id: LAYER_LINE_ID,
      type: 'line',
      source: SOURCE_ID,
      paint: {
        'line-color': palette.lineColor,
        'line-opacity': palette.lineOpacity,
        'line-width': palette.lineWidth,
      },
    })

    return () => {
      initializedRef.current = false
      // BEAD-0037: skip teardown if MapView's map.remove() already ran on sign-out /
      // basemap swap (getLayer would throw on the dropped style and blank the app).
      if (isMapAlive(map)) {
        for (const id of ALL_LAYER_IDS) {
          if (map.getLayer(id)) map.removeLayer(id)
        }
        if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID)
      }
    }
  }, [map])

  // ── Fetch the isochrone when (location, minutes) change; clear when off ──
  useEffect(() => {
    if (!map) return
    const setData = (data: GeoJSON.Feature | GeoJSON.FeatureCollection) => {
      const source = map.getSource(SOURCE_ID) as GeoJSONSource | undefined
      source?.setData(data)
    }

    // Off, or no pin → clear the layer, no network call.
    if (minutes === null || !location) {
      setData(EMPTY_FC)
      retryRef.current = () => {}
      return
    }

    const { lat, lng } = location
    let controller = new AbortController()

    const run = () => {
      controller.abort()
      controller = new AbortController()
      const { signal } = controller
      fetchIsochrone(lat, lng, minutes, signal)
        .then((result) => {
          if (signal.aborted) return
          // null geojson = no reachable-area data → clear (renders nothing, not an error).
          setData(result.geojson ?? EMPTY_FC)
        })
        .catch((err: unknown) => {
          if ((err as Error)?.name === 'AbortError' || signal.aborted) return
          toast.error("Couldn't load the drive-time area.", {
            id: 'isochrone-fetch-error', // dedupe — a flapping fetch updates one toast
            duration: 8000, // transient — auto-dismiss so it can't linger
            action: { label: 'Retry', onClick: () => retryRef.current() },
          })
        })
    }

    // Retry targets the CURRENT (location, minutes) — retryRef is reassigned every run.
    retryRef.current = run
    run()

    return () => {
      controller.abort()
    }
  }, [map, location, minutes])

  return null
}
